import type { ListCurriculumsQuery } from '@/application/port/in/dto/CurriculumCommand';
import type { Page } from '@/domain/model/Page';
import type { SpringPage } from '@/infrastructure/http/types';
import type { $Fetch } from 'ofetch';
import type { CurriculumGateway } from '@/application/port/out/CurriculumGateway';
import type {
  CreateCurriculumCommand,
  UpdateCurriculumCommand,
} from '@/application/port/in/dto/CurriculumCommand';
import type { Curriculum } from '@/domain/model/Curriculum';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/curriculums';

export class HttpCurriculumGateway implements CurriculumGateway {
  constructor(private readonly client: $Fetch) {}

  async list(query: ListCurriculumsQuery): Promise<Page<Curriculum>> {
    const res = await this.client<ApiResponse<SpringPage<Curriculum>>>(PATH, { method: 'GET', params: query });
    assertApiSuccess(res);
    const page = res.data;
    return { content: page.content, totalElements: page.totalElements, totalPages: page.totalPages, number: page.number, size: page.size, first: page.first, last: page.last };
  }

  async listByCourse(courseId: string): Promise<Curriculum[]> {
    const res = await this.client<ApiResponse<Curriculum[]>>(
      `${PATH}/courses/${encodeURIComponent(courseId)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async getById(id: string): Promise<Curriculum> {
    const res = await this.client<ApiResponse<Curriculum>>(
      `${PATH}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateCurriculumCommand): Promise<Curriculum> {
    const res = await this.client<ApiResponse<Curriculum>>(PATH, {
      method: 'POST',
      body: command,
    });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, command: UpdateCurriculumCommand): Promise<Curriculum> {
    const res = await this.client<ApiResponse<Curriculum>>(
      `${PATH}/${encodeURIComponent(id)}`,
      { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async publish(id: string): Promise<Curriculum> {
    const res = await this.client<ApiResponse<Curriculum>>(
      `${PATH}/${encodeURIComponent(id)}/publish`,
      { method: 'POST' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async archive(id: string): Promise<Curriculum> {
    const res = await this.client<ApiResponse<Curriculum>>(
      `${PATH}/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    assertApiSuccess(res);
    return res.data;
  }
}
